import Service from '@ember/service';

export interface EnvironmentConfigSignature {
  modulePrefix: string;
  environment: string;
  rootURL: string;
  locationType: string;
  historySupportMiddleware: boolean;
  EmberENV: {
    FEATURES: Record<string, unknown>;
    EXTEND_PROTOTYPES: Record<string, unknown>;
    APP: Record<string, unknown>;
  };
  tioEnvironment: string;
  featureFlags: Record<string, boolean>;
  datadog: { applicationId: string; clientToken: string };
  telemetry?: { endpoint: string; token?: string; enabled?: boolean; opensearchUrl?: string };
  apiKey: string;
  apiHost: string;
  employeeUrl?: string;
}

/**
 * Typed capability keys. A curated union (NOT auto-derived from per-app
 * `featureFlags`, which diverge per app + per environment) covering what the
 * capability layer actually gates today; grow it additively as flags are
 * promoted to capabilities. `can()` also accepts a bare `string` for app-specific
 * flags + back-compat, so the union is autocomplete/typo-help, not a hard wall.
 */
export type CapabilityKey =
  | 'tuitionAssistance'
  | 'studentLoanRepayment'
  | 'careerPathways'
  | 'financialWellness'
  | 'pslf'
  | 'employerMatch'
  | 'betaExperience'
  | 'demoMode';

/** A source's answer for a key: `true`/`false`, or `undefined` to ABSTAIN
 *  (fall through to the next, lower-ranked source). */
export type CapabilityVerdict = boolean | undefined;

/**
 * A pluggable input to capability resolution. App-level services (demo, beta,
 * entitlements) and a future remote-config source implement this and push it in
 * via {@link FeaturesService.registerCapabilitySource} — so this tio-common
 * service never imports app code (inversion of control, like SessionContext).
 *
 * `resolve` MUST be synchronous: capabilities gate boot-time templates, so a
 * remote source has to pre-resolve into a sync snapshot rather than await.
 */
export interface CapabilitySource {
  /** Stable id, e.g. 'demo' | 'beta' | 'entitlement' | 'remote-config'. */
  readonly id: string;
  /** Higher wins. See {@link CAPABILITY_SOURCE_RANK}. */
  readonly rank: number;
  resolve(key: CapabilityKey | string): CapabilityVerdict;
}

/** Conventional ranks for the precedence chain: demo > beta > entitlement >
 *  remote-config > (built-in flag tier = 100) > (built-in default tier = 0). */
export const CAPABILITY_SOURCE_RANK = {
  demo: 400,
  beta: 300,
  entitlement: 200,
  remoteConfig: 150,
} as const;

/** Non-`false` defaults for capability keys (the DEFAULT tier). Anything not
 *  listed floors to `false`, matching `isEnabled`'s `|| false` semantics. */
const CAPABILITY_DEFAULTS: Partial<Record<CapabilityKey, boolean>> = {};

export default class FeaturesService extends Service {
  ENV: EnvironmentConfigSignature | null = null;

  /** Registered capability sources, kept sorted by descending rank. */
  #sources: CapabilitySource[] = [];

  get tioEnvironment() {
    return this.ENV?.tioEnvironment || 'local';
  }

  get isAdminApp() {
    return this.ENV?.modulePrefix === 'tio-admin';
  }

  get isEmployeeApp() {
    return this.ENV?.modulePrefix === 'tio-employee';
  }

  get isAgentApp() {
    return this.ENV?.modulePrefix === 'tio-agent';
  }

  isEnabled = (feature: string): boolean => {
    if (!this.ENV) {
      throw 'ENV does not exist on FeaturesService. You must call `initWithEnvironment` from consuming app and pass the ENV.';
    }
    return this.ENV?.featureFlags?.[feature] || false;
  };

  isDisabled = (feature: string): boolean => {
    return !this.isEnabled(feature);
  };

  /**
   * Register a capability source. Sources are consulted by {@link can} in
   * descending rank order. Returns an unregister function (handy for tests and
   * teardown).
   *
   * Idempotent by `id`: registering a source whose `id` matches an existing one
   * REPLACES it (rather than stacking a duplicate), so a provider re-registering
   * a fresh instance — e.g. if `beforeModel` runs twice — can't end up consulted
   * multiple times with confusing precedence.
   */
  registerCapabilitySource = (source: CapabilitySource): (() => void) => {
    this.#sources = [...this.#sources.filter((s) => s.id !== source.id), source].sort(
      (a, b) => b.rank - a.rank
    );
    return () => {
      this.#sources = this.#sources.filter((s) => s !== source);
    };
  };

  /**
   * Resolve a capability with precedence: registered sources (highest rank
   * first; e.g. demo > beta > entitlement > remote-config), then the static
   * feature FLAG tier (`ENV.featureFlags[key]`), then the DEFAULT tier
   * ({@link CAPABILITY_DEFAULTS}, floor `false`). The first source returning a
   * non-`undefined` verdict wins.
   *
   * Unlike {@link isEnabled}, `can` does NOT throw when ENV is unseeded — it
   * returns the default — so it is safe for boot-time template gates. Use
   * `isEnabled` for a raw flag read (with its throw-when-unseeded contract);
   * use `can` for a capability with a safe default.
   */
  can = (key: CapabilityKey | string): boolean => {
    for (const source of this.#sources) {
      const verdict = source.resolve(key);
      if (verdict !== undefined) {
        return verdict;
      }
    }
    const flag = this.ENV?.featureFlags?.[key as string];
    if (typeof flag === 'boolean') {
      return flag;
    }
    return CAPABILITY_DEFAULTS[key as CapabilityKey] ?? false;
  };

  /** Inverse of {@link can}. */
  cannot = (key: CapabilityKey | string): boolean => {
    return !this.can(key);
  };

  initWithEnvironment = (ENV: EnvironmentConfigSignature) => {
    this.ENV = ENV;
  };
}
